import React, { useEffect, useRef } from 'react';
import { Button, Flex, Stack, Text, SimpleGrid } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateSectionCreationData } from 'src/company/templates/types';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import SplitMarkdownEditor from 'src/company/templates/components/template-creation-flow/steps/SplitMarkdownEditor';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { PreserveAsIsToggle } from './PreserveAsIsToggle';

type Props = {
  section: TemplateSectionCreationData;
  onUpdateSection: (sectionId: string, updates: Partial<TemplateSectionCreationData>) => void;
  onToggleComplete: (sectionId: string) => void;
  isCompleted?: boolean;
  onSectionSplit: (sections: TemplateSectionCreationData[]) => void;
  placeholderImages?: MarkdownImage[];
};

export function SectionContentEditorPane({
  section,
  onUpdateSection,
  onToggleComplete,
  onSectionSplit,
  placeholderImages = [],
  isCompleted = false,
}: Props) {
  const [isInSplitMode, setIsInSplitMode] = React.useState(false);
  const splitSection = useRef([section]);

  useEffect(() => {
    setIsInSplitMode(false);
    splitSection.current = [section];
  }, [section]);

  const handleTogglePreserve = (preserveAsIs: boolean) => {
    onUpdateSection(section.id, { preserveAsIs });
  };

  return (
    <Stack gap="md" h="100%" style={{ minWidth: 0 }} flex={1}>
      <Flex align="center" justify="space-between" style={{ minWidth: 0 }}>
        <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10" style={{ minWidth: 0 }} truncate>
          {section.sectionHeader}
        </Text>
        <Flex align="center" gap="xs" style={{ flexShrink: 0 }} direction={'row'}>
          <Button
            variant={'secondary'}
            size="xs"
            onClick={() => setIsInSplitMode(!isInSplitMode)}
            leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
          >
            {isInSplitMode ? 'Cancel split' : 'Split'}
          </Button>
          {isInSplitMode && (
            <Button
              variant={'primary'}
              size="xs"
              onClick={() => {
                onSectionSplit(splitSection.current);
                setIsInSplitMode(false);
              }}
              leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
            >
              Confirm split
            </Button>
          )}
          {!isInSplitMode && (
            <Button
              variant={isCompleted ? 'secondary' : 'primary'}
              size="xs"
              onClick={() => onToggleComplete(section.id)}
              leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
            >
              {isCompleted ? 'Mark incomplete' : 'Mark complete'}
            </Button>
          )}
        </Flex>
      </Flex>

      {isInSplitMode && (
        <SplitMarkdownEditor
          section={section}
          onSectionSplit={sections => {
            splitSection.current = sections;
          }}
          placeholderImages={placeholderImages}
        />
      )}

      {!isInSplitMode && (
        <>
          <PreserveAsIsToggle checked={section.preserveAsIs} onChange={handleTogglePreserve} />
          <SimpleGrid cols={2} spacing="md" key={section.id} h="100%">
            <MarkdownEditor
              label="Template"
              defaultValue={section.templatedMarkdown}
              onChange={value => onUpdateSection(section.id, { templatedMarkdown: value })}
              h="100%"
              placeholderImages={placeholderImages}
            />
            <MarkdownEditor
              label="Populated"
              defaultValue={section.populatedMarkdown}
              onChange={value => onUpdateSection(section.id, { populatedMarkdown: value })}
              h="100%"
              placeholderImages={placeholderImages}
            />
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}
